import { parseJsonBody, readVersionFromEtag, send } from './saveHttp';
import type {
    CreateStructureSetRequest,
    SetApprovalRequest,
    StructureSetDetailResponse,
    UpdateStructureRequest,
} from './connector-next-schemas';

/**
 * Hand-written client for the connector-next structure-set save endpoints.
 * HTTP conventions (If-Match, 409/428 mapping, ETag reading, PHI-safe
 * problem parsing) live in `saveHttp.ts`, shared with the other dataset
 * save clients.
 */

async function parseDetail(
    response: Response
): Promise<StructureSetDetailResponse> {
    return parseJsonBody<StructureSetDetailResponse>(response);
}

/** The structure PATCH result: the set's new version, from the ETag header. */
export type StructureUpdateResult = {
    version: string;
};

/**
 * POST /api/structure-sets — create an empty RTSTRUCT skeleton. The SOP
 * Instance UID in the request is the server's idempotency key: a 201 is a
 * row this request created, a 200 is the PRIOR row that already carried
 * the UID, returned for adoption. The distinction travels as `wasNew` —
 * the caller must not treat an adopted row's live version token as its
 * own.
 */
export async function postStructureSetSkeleton(
    request: CreateStructureSetRequest
): Promise<{ detail: StructureSetDetailResponse; wasNew: boolean }> {
    const response = await send('/api/structure-sets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });
    return {
        detail: await parseDetail(response),
        wasNew: response.status === 201,
    };
}

/**
 * PUT /{id}/content — the whole RTSTRUCT, with the state the file cannot
 * carry riding the same form. The server re-derives its ROI rows from the
 * file, and DICOM defines no attribute for any of these:
 *
 * - the locked ROI numbers, sent on every save (empty when nothing is
 *   locked). The field's presence is what declares the set complete: the
 *   server keeps the stored locks for an upload that omits it, so an unlock
 *   reaches the database only by being stated.
 * - which structures the AI model produced and which of those have had their
 *   contours edited, likewise sent on every save.
 * - `clientUserName`, who is saving, for the connector to record when it
 *   cannot establish the caller itself.
 */
export async function putStructureSetContent(
    id: string,
    ifMatch: string,
    file: Blob,
    beside: {
        readonly lockedRoiNumbers: readonly number[];
        readonly modelStructures: {
            readonly modelGenerated: readonly number[];
            readonly contourEdited: readonly number[];
        };
        readonly clientUserName?: string;
    }
): Promise<StructureSetDetailResponse> {
    const form = new FormData();
    form.append('file', file, 'structure-set.dcm');
    form.append('lockedRoiNumbers', beside.lockedRoiNumbers.join(','));
    form.append(
        'modelGeneratedRoiNumbers',
        beside.modelStructures.modelGenerated.join(',')
    );
    form.append(
        'contourEditedRoiNumbers',
        beside.modelStructures.contourEdited.join(',')
    );
    if (beside.clientUserName) {
        form.append('clientUserName', beside.clientUserName);
    }
    const response = await send(
        `/api/structure-sets/${id}/content`,
        { method: 'PUT', body: form },
        ifMatch
    );
    return parseDetail(response);
}

export async function postStructureSetApproval(
    id: string,
    ifMatch: string,
    request: SetApprovalRequest
): Promise<StructureSetDetailResponse> {
    const response = await send(
        `/api/structure-sets/${id}/approval`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        },
        ifMatch
    );
    return parseDetail(response);
}

/**
 * The structure PATCH is the one structure-set save endpoint whose body (a
 * StructureResponse) carries no id/version — the new version travels only
 * in the ETag.
 */
export async function patchStructureSetStructure(
    id: string,
    ifMatch: string,
    roiNumber: number,
    request: UpdateStructureRequest
): Promise<StructureUpdateResult> {
    const response = await send(
        `/api/structure-sets/${id}/structures/${roiNumber}`,
        {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        },
        ifMatch
    );
    return { version: readVersionFromEtag(response) };
}

export async function deleteStructureSet(id: string): Promise<void> {
    await send(`/api/structure-sets/${id}`, { method: 'DELETE' });
}

export async function getStructureSetDetail(
    id: string
): Promise<StructureSetDetailResponse> {
    const response = await send(`/api/structure-sets/${id}`, {
        method: 'GET',
    });
    return parseDetail(response);
}
